import React from "react";
import { generateMeritValuesPDF } from "./printUtils";

const Filters = ({ table, originalData, description }) => {
  const [isUpdating, setIsUpdating] = React.useState(false);

  // Get all currently active filters
  const getActiveFilters = () => {
    const filters = {};
    ["year", "municipality", "school", "program"].forEach((columnId) => {
      const value = table.getColumn(columnId)?.getFilterValue();
      if (value) filters[columnId] = value;
    });
    return filters;
  };

  // Get available values for a column based on active filters
  const getFilteredValues = (columnId) => {
    const activeFilters = getActiveFilters();

    // Define filter hierarchy for filtering available values
    const filterHierarchy = {
      year: [], // Year shows all available years
      municipality: ["year"], // Municipality depends on year
      school: ["year", "municipality"], // School depends on year and municipality
      program: ["year", "municipality", "school"], // Program depends on all above
    };

    // Get dependencies for current column
    const dependencies = filterHierarchy[columnId] || [];

    // Filter the original data based on dependencies
    const filteredRows = originalData.filter((row) => {
      return dependencies.every((dependencyId) => {
        const filterValue = activeFilters[dependencyId];
        return !filterValue || row[dependencyId] === filterValue;
      });
    });

    // Get unique values from filtered rows
    const uniqueValues = [
      ...new Set(filteredRows.map((row) => row[columnId])),
    ].filter(Boolean);

    // Sort values - special handling for year column
    return columnId === "year"
      ? uniqueValues.sort((a, b) => b - a) // Sort years in descending order
      : uniqueValues.sort(); // Keep alphabetical sort for other columns
  };

  // Handle print button click
  const handlePrint = () => {
    const filteredRows = table.getFilteredRowModel().rows;
    const data = filteredRows.map((row) => row.original);
    const activeFilters = getActiveFilters();
    generateMeritValuesPDF(data, activeFilters);
  };

  // Reset all filters except year
  const resetFilters = () => {
    ["municipality", "school", "program"].forEach((columnId) => {
      table.getColumn(columnId)?.setFilterValue("");
    });

    // Make sure year is set to latest year
    const yearColumn = table.getColumn("year");
    const availableYears = getFilteredValues("year");
    if (yearColumn && availableYears.length > 0) {
      const latestYear = Math.max(...availableYears).toString();
      yearColumn.setFilterValue(latestYear);
    }
  };

  // Add effect to set loaded class when filters have values
  React.useEffect(() => {
    const hasValues = ["year", "municipality", "school", "program"].some(
      (columnId) => {
        return getFilteredValues(columnId).length > 0;
      }
    );

    const appElement = document.getElementById("nobox-merit-app");
    if (hasValues && appElement) {
      appElement.classList.add("loaded");
    }
  }, [table.getFilteredRowModel().rows]);

  // Add effect to handle filter changes
  React.useEffect(() => {
    const tableContainer = document.querySelector(".table-container");
    if (tableContainer && !isUpdating) {
      setIsUpdating(true);
      tableContainer.classList.add("updating");

      // Remove updating class after a shorter delay
      setTimeout(() => {
        tableContainer.classList.remove("updating");
        setIsUpdating(false);
      }, 150);
    }
  }, [table.getFilteredRowModel().rows]);

  // Handle filter change and reset dependent filters
  const handleFilterChange = (columnId, value) => {
    const column = table.getColumn(columnId);
    if (!column) return;

    // Define which filters should be reset when each filter changes
    const dependentFilters = {
      year: ["municipality", "school", "program"],
      municipality: ["school", "program"],
      school: ["program"],
      program: [],
    };

    // Set the new value for current filter
    column.setFilterValue(value);

    // Reset dependent filters
    const filtersToReset = dependentFilters[columnId] || [];
    filtersToReset.forEach((filterId) => {
      table.getColumn(filterId)?.setFilterValue("");
    });
  };

  return (
    <div className="flex flex-col desktop:flex-row gap-4 mb-4 relative z-[60]">
      <div className="">
        <div className="flex flex-col gap-3">
          {[
            ["year", "municipality"],
            ["school", "program"],
          ].map((row, rowIndex) => (
            <div key={rowIndex} className="flex flex-col gap-3 md:flex-row">
              {row.map((columnId) => {
                const column = table.getColumn(columnId);
                const availableValues = getFilteredValues(columnId);
                const isActive = column?.getFilterValue();

                // If it's year column, set default to latest year
                const defaultValue =
                  columnId === "year" && availableValues.length > 0
                    ? Math.max(...availableValues).toString()
                    : "";

                // Set specific widths based on column type
                const widthClass =
                  columnId === "year"
                    ? "w-full md:w-[150px]"
                    : columnId === "program"
                    ? "w-full md:w-[200px]"
                    : "w-full md:w-[300px]";

                return (
                  <select
                    key={columnId}
                    className={`p-2 pr-8 border rounded select-custom ${widthClass} ${
                      isActive ? "bg-blue-50 border-blue-200" : ""
                    }`}
                    value={column?.getFilterValue() ?? defaultValue}
                    onChange={(e) =>
                      handleFilterChange(columnId, e.target.value)
                    }
                  >
                    <option value="">
                      Alla {column?.columnDef.pluralName}
                    </option>
                    {availableValues.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={resetFilters}
            className="w-auto h-auto mt-4 wp-block-button__link min-w-fit md:col-span-2"
          >
            Rensa filter
          </button>
          <button
            onClick={handlePrint}
            className="w-auto h-auto mt-4 wp-block-button__link min-w-fit md:col-span-2"
          >
            Skriv ut
          </button>
        </div>
      </div>
      {description && (
        <div
          className="flex flex-wrap content-end justify-end w-full [&_p]:text-xs [&_p]:last:mb-0"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
    </div>
  );
};

export default Filters;
